import React from 'react';
import { Gavel as RuleIcon } from '@mui/icons-material';
import { Box, Card, CardActionArea, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';

import { SimpleMarkdownRenderer } from '@components/Markdown/SimpleMarkdownRenderer';
import type { ComplianceRuleDocument } from '../types';

interface ComplianceRuleCardProps {
  rule: ComplianceRuleDocument;
  isGlobal: boolean;
  isDisabled?: boolean;
  variant?: 'preview' | 'full';
  onClick?: () => void;
}

export const ComplianceRuleCard: React.FC<ComplianceRuleCardProps> = ({ 
  rule, 
  isGlobal,
  isDisabled = false,
  variant = 'full',
  onClick 
}) => {
  const isPreview = variant === 'preview';

  const CardWrapper = ({ children }: { children: React.ReactNode }) => (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        opacity: isDisabled ? 0.6 : 1,
        '&:hover': onClick ? {
          boxShadow: 1,
          borderColor: 'borderColors.brand',
          cursor: 'pointer'
        } : undefined,
      }}
    >
      {onClick ? (
        <CardActionArea onClick={onClick} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}>
          {children}
        </CardActionArea>
      ) : children}
    </Card>
  );

  return (
    <CardWrapper>
      {/* Header Section with Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <RuleIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.25} flexWrap="wrap">
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                lineHeight: 1.25,
                color: 'textColors.darkText',
                // Clamp title lines in preview
                ...(isPreview && {
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                })
              }}
            >
              {rule.title || rule.selector_text || 'Untitled Rule'}
            </Typography>
            {rule.state === 'deprecated' && (
              <Chip 
                label="Deprecated" 
                size="small" 
                sx={{ height: 16, fontSize: '0.625rem' }} 
                color="default"
                variant="outlined"
              />
            )}
            {isDisabled && (
              <Chip 
                label="Disabled" 
                size="small" 
                sx={{ height: 16, fontSize: '0.625rem' }} 
                color="default"
                variant="outlined"
              />
            )}
            {!isDisabled && isGlobal && (
              <Chip 
                label="Global" 
                size="small" 
                sx={{ height: 16, fontSize: '0.625rem' }} 
                color="secondary"
                variant="outlined"
              />
            )}
            {!isDisabled && !isGlobal && (
              <Chip 
                label="Organization" 
                size="small" 
                sx={{ height: 16, fontSize: '0.625rem' }} 
                color="primary"
                variant="outlined"
              />
            )}
            {rule.always_select && (
              <Chip 
                label="Always Select" 
                size="small" 
                sx={{ height: 16, fontSize: '0.625rem' }} 
                color="info"
                variant="outlined"
              />
            )}
          </Stack>
          {/* Display facets as chips */}
          {(rule.facets?.payer?.length || rule.facets?.cpt?.length || rule.facets?.provider_type?.length) && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.25}>
              {rule.facets.payer?.map((payer) => (
                <Chip
                  key={payer}
                  label={payer}
                  size="small"
                  sx={{ height: 14, fontSize: '0.625rem' }}
                  variant="outlined"
                />
              ))}
              {rule.facets.cpt?.map((cpt) => (
                <Chip
                  key={cpt}
                  label={`CPT ${cpt}`}
                  size="small"
                  sx={{ height: 14, fontSize: '0.625rem' }}
                  variant="outlined"
                />
              ))}
              {rule.facets.provider_type?.map((providerType) => (
                <Chip
                  key={providerType}
                  label={providerType}
                  size="small"
                  sx={{ height: 14, fontSize: '0.625rem' }}
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', width: '100%' }}>
        <Stack spacing={1}>
          {/* Body Markdown - Use SimpleMarkdownRenderer for lightweight rendering */}
          {isPreview ? (
            <Box
              sx={{ 
                maxHeight: '120px',
                overflow: 'hidden',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '40px',
                  background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.9))',
                  pointerEvents: 'none',
                }
              }}
            >
              <SimpleMarkdownRenderer markdown={rule.body_md || ''} />
          </Box>
          ) : (
            <SimpleMarkdownRenderer markdown={rule.body_md || ''} />
          )}
        </Stack>
      </CardContent>

      {/* Footer with Source Information */}
      {(rule.source?.document_title || rule.source?.document_date) && (
      <Box
        sx={{
          px: 1.5,
          py: 1,
          mt: 'auto',
          bgcolor: 'backgroundColors.secondary',
          borderTop: '1px solid',
          borderColor: 'borderColors.primary',
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'textColors.lightText', 
            fontSize: '0.6875rem', 
            fontStyle: 'italic',
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
            Source: <strong style={{ color: 'inherit' }}>{rule.source?.document_title || 'Unknown'}</strong>
            {rule.source?.document_date && ` • ${rule.source.document_date}`}
        </Typography>
      </Box>
      )}
    </CardWrapper>
  );
};
